import React from 'react';
import { Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Graduation } from '../../hooks/api/user';
import { formatFullDate, getGraduationLabel } from './studentGraduation';

interface GraduationHistoryTableProps {
  graduationHistory: Graduation[];
}

export default function GraduationHistoryTable({ graduationHistory }: GraduationHistoryTableProps) {
  const empty = (
    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
      Aún no hay graduaciones registradas para este alumno.
    </Typography>
  );

  return (
    <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid rgba(18,18,18,0.08)', overflow: 'hidden' }}>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 }, pb: { xs: 1, md: 1.5 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Historial de graduaciones
        </Typography>
      </Box>

      {/* Mobile: card list */}
      <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
        {graduationHistory.length ? (
          graduationHistory.map((graduation, index) => (
            <Box
              key={graduation.id}
              sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid rgba(18,18,18,0.06)',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                    {formatFullDate(graduation.graduatedAt)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {getGraduationLabel(graduation.newBelt, graduation.newStripes)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    antes: {getGraduationLabel(graduation.previousBelt, graduation.previousStripes)}
                  </Typography>
                  {graduation.note && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                      "{graduation.note}"
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {graduation.classCountAtGraduation}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    clases
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>{empty}</Box>
        )}
      </Box>

      {/* Desktop: full table */}
      <TableContainer sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#111111' }}>
              <TableCell sx={{ color: 'white', borderBottom: 'none', fontWeight: 600 }}>Fecha</TableCell>
              <TableCell sx={{ color: 'white', borderBottom: 'none', fontWeight: 600 }}>Anterior</TableCell>
              <TableCell sx={{ color: 'white', borderBottom: 'none', fontWeight: 600 }}>Nueva graduación</TableCell>
              <TableCell sx={{ color: 'white', borderBottom: 'none', fontWeight: 600 }}>Clases</TableCell>
              <TableCell sx={{ color: 'white', borderBottom: 'none', fontWeight: 600, display: { sm: 'none', md: 'table-cell' } }}>
                Nota
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {graduationHistory.length ? (
              graduationHistory.map((graduation) => (
                <TableRow key={graduation.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.06)', whiteSpace: 'nowrap' }}>
                    {formatFullDate(graduation.graduatedAt)}
                  </TableCell>
                  <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.06)', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {getGraduationLabel(graduation.previousBelt, graduation.previousStripes)}
                  </TableCell>
                  <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.06)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {getGraduationLabel(graduation.newBelt, graduation.newStripes)}
                  </TableCell>
                  <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.06)' }}>
                    <Typography fontWeight={600}>{graduation.classCountAtGraduation}</Typography>
                  </TableCell>
                  <TableCell sx={{ borderBottomColor: 'rgba(18,18,18,0.06)', color: 'text.secondary', display: { sm: 'none', md: 'table-cell' } }}>
                    {graduation.note || '—'}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} sx={{ borderBottom: 0 }}>{empty}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
